// This is a configration file for cancellation workflows
import React, { useEffect, useState, useCallback } from 'react';
import { post } from './api.js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { convertUNIXToDate } from './util.js';
import { findContactWithMentionedSubscription } from './util.js';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import GeneratedResponse from './GeneratedResponse.js';
import { track } from '@amplitude/analytics-browser';

const State = {
  BEGIN: 1,
  ACTION: 2,
  RESOLUTION: 3,
};

export function extractEnvironmentData(contact, sub) {
  return {
    email: contact.email,
    onboarded: contact.onboarded,
    subscription: {
      id: sub.id,
      plan: sub.plan.interval,
      trial_end: sub.trial_end,
      status: sub.status,
      cancel_at_period_end: sub.cancel_at_period_end,
      customer: sub.customer,
      current_period_end: sub.current_period_end,
    },
  };
}

function getUpdatedData(data, subscription) {
  const subId = subscription.id;
  if (subId === data.subscription.id) {
    const keys = Object.keys(data.subscription);
    for (const key of keys) {
      data.subscription[key] = subscription[key];
    }
  }
  return data;
}

// Conditions
const isCancelled = (data) => {
  return data.subscription != null && data.subscription.status === 'canceled';
};

const cancelAtPeriodEndTrial = (data) => {
  return (
    data.subscription != null &&
    data.subscription.status === 'trialing' &&
    data.subscription.cancel_at_period_end === true
  );
};

const cancelAtPeriodEndActive = (data) => {
  return (
    data.subscription != null &&
    data.subscription.status === 'active' &&
    data.subscription.cancel_at_period_end === true
  );
};

const eligibleForRefund = (data) => {
  return (
    data.subscription != null &&
    data.subscription.status === 'active' &&
    (data.subscription.plan === 'month' ||
      (data.subscription.plan === 'year' && data.onboarded !== true))
  );
};

const eligibleForRefundAlreadyCancelingAtPeriodEnd = (data) => {
  return eligibleForRefund(data) && cancelAtPeriodEndActive(data);
};

const isActiveYear = (data) => {
  return (
    data.subscription != null &&
    data.subscription.status === 'active' &&
    data.subscription.plan === 'year'
  );
};
const isTrialing = (data) => {
  return data.subscription != null && data.subscription.status === 'trialing';
};
const isPastDue = (data) => {
  return data.subscription != null && data.subscription.status === 'past_due';
};
const noSub = (data) => {
  return data.subscription == null;
};

const getMissingAccountMsg = (data) => {
  return `Happy to help! I can't find any subscription under ${data.email}. This may be because your account is associated with a different email. Could you provide us with one (or more) of the following to help us find your account: invoice, invoice number, receipt, any other email you might have registered with?`;
};

const getAlreadyCancelledMessage = (data) => {
  return `Your subscription under ${data.email} is already canceled and you will not be charged. Thanks for trying Motion!`;
};

const getRefundMessage = (data) => {
  return "No problem! I've processed a one-time refund for your payment and canceled your subscription. (We've already sent the refund money back to your bank - it may take your bank a few days to process it)\nThanks for trying Motion!";
};

const getNoPathMsg = (data) => {
  return 'No path configured for this condition';
};

const annualNonRefundable = (data) => {
  return 'No response recommended. Special review is needed because this is a annual subscription and user is onboarded';
};

const cancelPastDueMsg = (data) => {
  return 'I just canceled your subscription and no more charges will be attempted. Thanks for trying Motion!';
};

const cancelCurrentMsg = (data) => {
  return 'I have canceled your subscription. Thanks for trying Motion!';
};

const cancelTrialEndMessage = (data) => {
  return `I have updated your subscription to cancel at ${convertUNIXToDate(
    data.subscription.trial_end,
  )} (the end of the trial period), and you will not be charged. Thanks for trying Motion!`;
};

const alreadyCancelAtPeriodEnd = (data) => {
  const periodEnd = convertUNIXToDate(data.subscription.current_period_end);
  if (data.subscription.status === 'active') {
    return `I can confirm that your subscription is scheduled to cancel on ${periodEnd} (end of current period), and you will not be charged. Thanks for trying Motion!`;
  } else if (data.subscription.status === 'trialing') {
    return `Your subscription is scheduled to cancel on ${periodEnd} (end of the current trial period). I can confirm that you will not be charged. Thanks for trying Motion!`;
  }
};

export const CancelConfig = {
  action: [
    'cancel_now',
    'cancel_now_past_due',
    'cancel_at_period_end',
    'cancel_now_and_refund',
  ],
  path: [
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.ACTION,
          action: 'cancel_now_and_refund',
        },
      ],
      condition: eligibleForRefundAlreadyCancelingAtPeriodEnd,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.ACTION,
          action: 'cancel_now_and_refund',
        },
        { state: State.ACTION, action: 'cancel_at_period_end' },
      ],
      condition: eligibleForRefund,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.RESOLUTION,
          msg: alreadyCancelAtPeriodEnd,
        },
        { state: State.ACTION, action: 'cancel_now_and_refund' },
      ],
      condition: cancelAtPeriodEndActive,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.RESOLUTION,
          msg: alreadyCancelAtPeriodEnd,
        },
        {
          state: State.ACTION,
          action: 'cancel_now',
        },
      ],
      condition: cancelAtPeriodEndTrial,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.RESOLUTION,
          msg: getMissingAccountMsg,
        },
      ],
      condition: noSub,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.RESOLUTION,
          msg: getAlreadyCancelledMessage,
        },
      ],
      condition: isCancelled,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.RESOLUTION,
          msg: annualNonRefundable,
        },
        { state: State.ACTION, action: 'cancel_now_and_refund' },
        { state: State.ACTION, action: 'cancel_at_period_end' },
      ],
      condition: isActiveYear,
    },
    {
      begin: { state: State.BEGIN },
      end: [
        {
          state: State.ACTION,
          action: 'cancel_at_period_end',
        },
        {
          state: State.ACTION,
          action: 'cancel_now',
        },
      ],
      condition: isTrialing,
    },
    {
      begin: { state: State.BEGIN },
      end: [{ state: State.ACTION, action: 'cancel_now_past_due' }],
      condition: isPastDue,
    },
    {
      begin: { state: State.ACTION, action: 'cancel_now_past_due' },
      condition: isCancelled,
      end: [
        {
          state: State.RESOLUTION,
          msg: cancelPastDueMsg,
        },
      ],
    },
    {
      begin: { state: State.ACTION, action: 'cancel_now' },
      condition: isCancelled,
      end: [
        {
          state: State.RESOLUTION,
          msg: cancelCurrentMsg,
        },
      ],
    },
    {
      begin: { state: State.ACTION, action: 'cancel_now_and_refund' },
      condition: isCancelled,
      end: [
        {
          state: State.RESOLUTION,
          msg: getRefundMessage,
        },
      ],
    },
    {
      begin: { state: State.ACTION, action: 'cancel_at_period_end' },
      condition: cancelAtPeriodEndTrial,
      end: [
        {
          state: State.RESOLUTION,
          msg: cancelTrialEndMessage,
        },
      ],
    },
    {
      begin: { state: State.ACTION, action: 'cancel_at_period_end' },
      end: [
        {
          state: State.RESOLUTION,
          msg: alreadyCancelAtPeriodEnd,
        },
      ],
      condition: cancelAtPeriodEndActive,
    },
  ],
  defaultResolution: {
    end: [
      {
        state: State.RESOLUTION,
        msg: getNoPathMsg,
      },
    ],
  },
};

// All actions have to return an updated subscription
export const ActionConfig = {
  cancel_now: (data) => {
    return post('cancel_sub', {
      sub_id: data.subscription.id,
    });
  },
  cancel_now_past_due: (data) => {
    return post('cancel_sub', {
      sub_id: data.subscription.id,
    });
  },
  cancel_at_period_end: (data) => {
    return post('update_sub', {
      sub_id: data.subscription.id,
      updateParams: { cancel_at_period_end: true },
    });
  },
  cancel_now_and_refund: async (data) => {
    const refund = await post('refund', {
      cus_id: data.subscription.customer,
      sub_id: data.subscription.id,
    });
    if (refund.status === 201) {
      return await post('cancel_sub', {
        sub_id: data.subscription.id,
      });
    }
  },
};

function matchState(currentState, state) {
  return JSON.stringify(currentState) === JSON.stringify(state);
}

function execute(currentState, data) {
  if (data == null) {
    return null;
  }
  const path = CancelConfig.path.find((s) => {
    return matchState(s.begin, currentState) && s.condition(data);
  });
  if (path == null) {
    return CancelConfig.defaultResolution;
  }
  return path;
}

function Cancel({ latestInbound, contacts }) {
  const [currentState, setCurrentState] = useState({ state: State.BEGIN });
  const [prevState, setPrevState] = useState();
  const [availableStates, setAvailableStates] = useState([
    { state: State.BEGIN },
  ]);
  const [data, setData] = useState();
  const [readyToExecute, setReadyToExecute] = useState(true);

  useEffect(() => {
    if (contacts != null && Object.keys(contacts).length > 0) {
      const subscription = findContactWithMentionedSubscription(contacts);
      const currentData =
        subscription == null
          ? { email: Object.keys(contacts)[0] }
          : extractEnvironmentData(subscription[0], subscription[1]);
      console.log(currentData);
      setData(currentData);
    }
  }, [contacts]);
  console.log(data);

  useEffect(() => {
    if (currentState != null && readyToExecute) {
      const path = execute(currentState, data);
      if (path == null) {
        return;
      }
      setPrevState(path.begin);
      setAvailableStates(path.end);
      setReadyToExecute(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToExecute, data]);

  const suggestedBlock = useCallback(
    (state, isOther) => {
      if (state.state === State.ACTION) {
        const actionName = state.action;
        const fn = ActionConfig[actionName];
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              marginBottom: '8px',
            }}
          >
            <span>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                  textAlign: 'left',
                  marginRight: '6px',
                }}
              >
                <strong>
                  {isOther ? 'Other action: ' : 'Suggested action: '}
                </strong>
              </Typography>
            </span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={async () => {
                track('ACTION_' + actionName);
                setCurrentState(state);
                const result = await fn(data);
                if (result.status === 201) {
                  setData(getUpdatedData(data, result.data));
                  setReadyToExecute(true);
                }
              }}
            >
              {actionName}
            </Button>
          </div>
        );
      } else if (state.state === State.RESOLUTION) {
        const msg = state.msg(data);
        return (
          <div
            style={{
              marginBottom: '12px',
              borderBottom: '1px double #112F82',
            }}
          >
            <GeneratedResponse
              generated={msg}
              latestInbound={latestInbound}
              source="action"
            />
          </div>
        );
      } else {
        return null;
      }
    },
    [data, latestInbound],
  );

  const displayPrevAction = useCallback((prevState) => {
    if (prevState.action != null) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
          }}
        >
          <Button sx={{ ml: '-4px' }} isDisabled={true}>
            {prevState.action}
          </Button>
          <CheckCircleIcon color="primary" />
        </div>
      );
    }
    return null;
  }, []);

  if (availableStates == null) {
    return null;
  } else {
    return (
      <>
        {prevState != null &&
          prevState.action != null &&
          displayPrevAction(prevState)}
        {suggestedBlock(availableStates[0])}
        {availableStates.length > 1 && (
          <>{availableStates.slice(1).map((s) => suggestedBlock(s, true))}</>
        )}
      </>
    );
  }
}
export default Cancel;
